import { Box, Button, Flex, Image, Stack, Text } from '@chakra-ui/react';
import { BookmarkSimple, QuestionMark } from '@phosphor-icons/react';
import { useCallback, useEffect, memo, useRef, useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiBookmarkTranscript } from '@/api/services/meeting.service';
import EmptyTranscriptImage from '@/assets/image/empty-transcript-cover.svg';
import { MeetingTranscriptBookmark } from '@/types/enums';
import { Transcript, Meeting } from '@/types/Meeting';
import ImportTranscriptDialog from './ImportTranscriptDialog';

const SPEAKER_COLORS = ['primary.900', 'neutrals.700', 'error.100', 'success.100'] as const;
const SCROLL_BEHAVIOR = 'smooth' as const;

const TranscriptItem = memo(
  ({
    transcript,
    onBookmark,
    getColorForSpeaker,
    formatTime
  }: {
    transcript: Transcript;
    onBookmark: (transcriptId: number, bookmark: MeetingTranscriptBookmark) => void;
    getColorForSpeaker: (speakerId: number) => string;
    formatTime: (timestamp: number) => string;
  }) => {
    const { transcript_id, speaker_name, speaker_id, start_time, text, bookmarked } = transcript;

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('');
    };

    return (
      <Flex
        mt={transcript_id === 0 ? '50px' : '0'}
        align="center"
        key={transcript_id}
        gap="12px"
        w="100%"
        position="relative"
        p="20px 12px"
        borderRadius="8px"
        cursor="pointer"
        bg={
          bookmarked === MeetingTranscriptBookmark.Saved
            ? 'primary.50'
            : bookmarked === MeetingTranscriptBookmark.Question
            ? 'error.100'
            : 'transparent'
        }
        _hover={{
          bg: 'neutrals.50',
          '& .transcript-actions': {
            opacity: 1
          }
        }}
        transition="background-color 0.2s ease-in-out"
        role="article"
        aria-label={`Transcript from ${speaker_name} at ${formatTime(start_time)}`}
      >
        <Box
          w="45px"
          h="45px"
          minW="45px"
          minH="45px"
          bg={getColorForSpeaker(speaker_id)}
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="12px"
          color="neutrals.0"
          fontWeight="bold"
          aria-hidden="true"
        >
          {getInitials(speaker_name)}
        </Box>
        <Stack gap="4px" w="100%">
          <Flex gap="8px" align="center" justify="space-between">
            <Flex gap="8px" align="center">
              <Text fontWeight="semibold" color="neutrals.900" fontSize="16px">
                {speaker_name}
              </Text>
              <Text color="neutrals.500" fontSize="14px">
                {formatTime(start_time)}
              </Text>
            </Flex>
            {bookmarked === MeetingTranscriptBookmark.Saved && (
              <Box title="Saved for later" aria-label="Saved for later">
                <BookmarkSimple size={16} weight="fill" color="#3C5CC6" />
              </Box>
            )}
            {bookmarked === MeetingTranscriptBookmark.Question && (
              <Box title="Marked for follow-up" aria-label="Marked for follow-up">
                <QuestionMark size={16} color="#F97066" />
              </Box>
            )}
            <Flex
              className="transcript-actions"
              opacity={0}
              transition="opacity 0.2s ease-in-out"
              position="absolute"
              right="0"
              top="-50px"
              border="1px solid"
              borderRadius="8px"
              borderColor="neutrals.300"
              bg="background.secondary"
              zIndex="10"
              boxShadow="sm"
              pointerEvents="auto"
              role="menu"
              aria-label="Transcript actions"
            >
              <Button
                m="4px"
                p="4px"
                variant="ghost"
                bg={bookmarked === MeetingTranscriptBookmark.Question ? 'error.100' : 'transparent'}
                _hover={{
                  bg: 'neutrals.50'
                }}
                title={
                  bookmarked === MeetingTranscriptBookmark.Question
                    ? 'Remove question mark'
                    : 'Mark for follow-up'
                }
                aria-label={
                  bookmarked === MeetingTranscriptBookmark.Question
                    ? 'Remove question mark'
                    : 'Mark for follow-up'
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onBookmark(
                    transcript_id,
                    bookmarked === MeetingTranscriptBookmark.Question
                      ? MeetingTranscriptBookmark.None
                      : MeetingTranscriptBookmark.Question
                  );
                }}
                role="menuitem"
              >
                {bookmarked === MeetingTranscriptBookmark.Question ? (
                  <QuestionMark size={12} color="#F97066" />
                ) : (
                  <QuestionMark size={12} color="#242424" />
                )}
              </Button>
              <Button
                m="4px"
                p="4px"
                variant="ghost"
                bg={bookmarked === MeetingTranscriptBookmark.Saved ? 'primary.100' : 'transparent'}
                _hover={{
                  bg: 'neutrals.50'
                }}
                title={
                  bookmarked === MeetingTranscriptBookmark.Saved
                    ? 'Remove bookmark'
                    : 'Save for later'
                }
                aria-label={
                  bookmarked === MeetingTranscriptBookmark.Saved
                    ? 'Remove bookmark'
                    : 'Save for later'
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onBookmark(
                    transcript_id,
                    bookmarked === MeetingTranscriptBookmark.Saved
                      ? MeetingTranscriptBookmark.None
                      : MeetingTranscriptBookmark.Saved
                  );
                }}
                role="menuitem"
              >
                {bookmarked === MeetingTranscriptBookmark.Saved ? (
                  <BookmarkSimple size={12} weight="fill" color="#3C5CC6" />
                ) : (
                  <BookmarkSimple size={12} color="#242424" />
                )}
              </Button>
            </Flex>
          </Flex>
          <Text
            color="neutrals.900"
            fontSize="16px"
            whiteSpace="pre-wrap"
            overflowWrap="break-word"
            lineHeight="1.5"
          >
            {text}
          </Text>
        </Stack>
      </Flex>
    );
  }
);

TranscriptItem.displayName = 'TranscriptItem';

const EmptyTranscript = ({
  meetingId,
  onUpdateMeeting,
  showImportMockDialog = false
}: {
  meetingId: string;
  onUpdateMeeting: (meeting: Meeting) => void;
  showImportMockDialog?: boolean;
}) => {
  return (
    <Stack justify="center" align="center" h="100%">
      <Image src={EmptyTranscriptImage} alt="Empty transcript" w="210px" h="210px" mb="24px" />
      <Text fontSize="24px" color="neutrals.800" fontWeight="semibold" mb="4px">
        Interview not started yet
      </Text>
      <Text fontSize="18px" color="neutrals.500" textAlign="center">
        Once the interview kicks off, all the details will show up here.
      </Text>
      {(import.meta.env.VITE_APP_ENV === 'development' ||
        import.meta.env.VITE_APP_ENV === 'debug') &&
        showImportMockDialog && (
          <Box mt="12px">
            <ImportTranscriptDialog meetingId={meetingId} onUpdateMeeting={onUpdateMeeting} />
          </Box>
        )}
    </Stack>
  );
};

export default function TranscriptList({
  meetingId,
  transcripts,
  scrollToBottom = false,
  showImportMockDialog = false
}: {
  meetingId: string;
  transcripts: Transcript[];
  scrollToBottom?: boolean;
  showImportMockDialog?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [transcriptList, setTranscriptList] = useState<Transcript[]>(transcripts);
  const [speakerColors, setSpeakerColors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (transcripts.length === 0) {
      setTranscriptList([]);
      return;
    }

    setTranscriptList((prevTranscripts) => {
      const bookmarkMap = new Map(
        prevTranscripts.map((prev) => [prev.transcript_id, prev.bookmarked])
      );

      return transcripts.map((newTranscript) => ({
        ...newTranscript,
        bookmarked: bookmarkMap.get(newTranscript.transcript_id) || newTranscript.bookmarked
      }));
    });
  }, [transcripts]);

  useEffect(() => {
    if (scrollToBottom && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [scrollToBottom, transcriptList.length]);

  useEffect(() => {
    const speakers = transcriptList.reduce<number[]>((acc, transcript) => {
      const speakerId = transcript.speaker_id;
      if (!acc.includes(speakerId)) {
        acc.push(speakerId);
      }
      return acc;
    }, []);

    const colors = speakers.reduce((acc, speakerId, index) => {
      acc[speakerId] = SPEAKER_COLORS[index % SPEAKER_COLORS.length];
      return acc;
    }, {} as Record<number, string>);

    setSpeakerColors(colors);
  }, [transcriptList]);

  const onUpdateMeeting = useCallback((meeting: Meeting) => {
    setTranscriptList(meeting.transcripts);
  }, []);

  const getColorForSpeaker = useCallback(
    (speakerId: number) => {
      return speakerColors[speakerId] || SPEAKER_COLORS[0];
    },
    [speakerColors]
  );

  const handleBookmarkTranscript = useCallback(
    async (transcriptId: number, bookmark: MeetingTranscriptBookmark) => {
      setTranscriptList((prevTranscripts) =>
        prevTranscripts.map((t) => {
          if (t.transcript_id === transcriptId) {
            return { ...t, bookmarked: bookmark };
          }
          return t;
        })
      );

      try {
        const response = await fetchAPI<void>({
          request: () => apiBookmarkTranscript(meetingId, { transcript_id: transcriptId, bookmark })
        });

        if (!response.success) {
          console.error('Error bookmarking transcript:', response.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error bookmarking transcript:', error);
      }
    },
    [meetingId]
  );

  const formatTime = useCallback((timestamp: number) => {
    return new Date(timestamp * 1000).toISOString().slice(14, 19);
  }, []);

  return (
    <Stack
      ref={containerRef}
      gap="4px"
      w="100%"
      h="100%"
      overflowY="auto"
      scrollBehavior={SCROLL_BEHAVIOR}
      role="log"
      aria-label="Meeting transcript"
    >
      {transcriptList.length === 0 ? (
        <EmptyTranscript
          meetingId={meetingId}
          onUpdateMeeting={onUpdateMeeting}
          showImportMockDialog={showImportMockDialog}
        />
      ) : (
        transcriptList.map((transcript) => (
          <TranscriptItem
            key={transcript.transcript_id}
            transcript={transcript}
            onBookmark={handleBookmarkTranscript}
            getColorForSpeaker={getColorForSpeaker}
            formatTime={formatTime}
          />
        ))
      )}
    </Stack>
  );
}
